// src/pages/kanjiLevels.js
// Public per-level kanji page (SEO Phase 2a). Renders from bundled static data
// (no API call, no login required) so it prerenders to real HTML. Was previously
// auth-gated (redirected to /login); now public for indexing.
import React from 'react';
import { usePostgresUser } from '../components/utils/PostgresUserContext';
import Navbar from '../components/navBar';
import Footer from '../components/footer';
import { Helmet } from 'react-helmet';
import { useParams, Link } from 'react-router-dom';
import kanjiStatic from '../data/kanjiStatic.json';

const TOTAL_LEVELS = kanjiStatic.maxLevel;

function LevelPage() {
  const { level } = useParams();
  const { user, subscriptionStatus } = usePostgresUser();

  const currentLevel = parseInt(level, 10);
  const kanjiList = kanjiStatic.byLevel[currentLevel] || [];
  const prevLevel = currentLevel - 1 > 0 ? currentLevel - 1 : null;
  const nextLevel = currentLevel + 1 <= TOTAL_LEVELS ? currentLevel + 1 : null;

  const canonical = `https://onikanji.com/levels/${currentLevel}`;
  const title = `Level ${currentLevel} Kanji — ${kanjiList.length} Japanese Kanji | OniKanji`;
  const description = kanjiList.length
    ? `Level ${currentLevel} of the OniKanji curriculum: ${kanjiList.length} kanji (${kanjiList.slice(0, 6).map((k) => k.c).join('、')}…). See the meaning of each, free.`
    : `Level ${currentLevel} of the OniKanji kanji curriculum.`;

  return (
    <div className="font-noto-sans-jp">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={`Level ${currentLevel} Kanji | OniKanji`} />
        <meta property="og:description" content={description} />
        <meta property="og:image" content="https://onikanji.com/og-image.png" />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="OniKanji" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`Level ${currentLevel} Kanji | OniKanji`} />
        <meta name="twitter:image" content="https://onikanji.com/og-image.png" />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'All Kanji', item: 'https://onikanji.com/kanji' },
            { '@type': 'ListItem', position: 2, name: `Level ${currentLevel}`, item: canonical },
          ],
        })}</script>
        {kanjiList.length > 0 && (
          <script type="application/ld+json">{JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            name: `Level ${currentLevel} Kanji`,
            numberOfItems: kanjiList.length,
            itemListElement: kanjiList.map((k, i) => ({
              '@type': 'ListItem',
              position: i + 1,
              name: `${k.c} (${k.m})`,
              url: `https://onikanji.com/kanji/${encodeURIComponent(k.c)}`,
            })),
          })}</script>
        )}
      </Helmet>

      <Navbar user={user} subscriptionStatus={subscriptionStatus} />

      <div className="container mx-auto space-y-4 p-6 max-sm:px-4 max-sm:py-4 sm:max-lg:!px-5 sm:max-lg:!py-4">
        <nav className="text-sm text-gray-500 mt-4 max-sm:mt-0">
          <Link to="/kanji" className="hover:underline">All Kanji</Link>
          <span className="mx-2">/</span>
          <span className="text-gray-700 font-medium">Level {currentLevel}</span>
        </nav>

        <div className="flex flex-col items-center bg-lightBlueBackground text-lightBlueText p-4 max-sm:py-3 rounded-lg mt-4 max-sm:mt-2 sm:max-lg:!py-3">
          <h1 className="text-2xl max-sm:text-xl md:text-3xl font-bold">Level {currentLevel} Kanji</h1>
          <div className="text-lg max-sm:text-base">{kanjiList.length} kanji</div>
        </div>

        {kanjiList.length > 0 ? (
          <div className="grid grid-cols-2 max-sm:grid-cols-3 max-sm:gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:max-md:!grid-cols-5 md:max-lg:!grid-cols-6 sm:max-lg:!gap-3 mt-6">
            {kanjiList.map((k) => (
              <Link
                to={`/kanji/${k.c}`}
                key={k.c}
                className="flex flex-col items-center p-4 max-sm:px-1.5 max-sm:py-3 max-sm:min-w-0 sm:max-lg:!px-2 sm:max-lg:!py-3 sm:max-lg:min-w-0 bg-white border rounded-lg shadow hover:bg-blue-100 transition duration-300"
              >
                <div className="text-4xl max-sm:text-3xl max-sm:mb-1 font-bold mb-2">{k.c}</div>
                <div className="text-gray-600 max-sm:text-xs max-sm:w-full max-sm:text-center max-sm:truncate sm:max-lg:w-full sm:max-lg:text-center sm:max-lg:truncate sm:max-lg:!text-sm">{k.m}</div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="text-center p-8 bg-yellow-100 rounded-lg mt-6">
            <p className="text-xl font-semibold text-yellow-800">
              This level is under rapid development. Please join the Discord for the latest updates and timelines.
            </p>
          </div>
        )}

        <div className="flex justify-between mt-6">
          {prevLevel ? (
            <Link to={`/levels/${prevLevel}`} className="px-4 py-2 rounded bg-blue-500 text-white">Previous Level</Link>
          ) : (
            <div />
          )}
          {nextLevel ? (
            <Link to={`/levels/${nextLevel}`} className="px-4 py-2 rounded bg-blue-500 text-white">Next Level</Link>
          ) : (
            <div />
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default LevelPage;
